import React, { useState, useEffect, useCallback } from 'react';
import MentionInput from './MentionInput';
import ImageDropZone from './ImageDropZone';
import { renderRichText } from '../../utils/richText';

/**
 * Shared comment list + composer — used on Announcements and Lessons
 * (Hive/Discussion has its own, slightly richer version with staff-post
 * fields, kept separate). Same @mention support everywhere via MentionInput.
 *
 * Replies (2026-08-31): one level of nesting — a reply belongs to a
 * top-level comment, not to another reply. This was in the original scope
 * for the comment build and never actually landed; `createComment` here
 * takes an optional second argument (parentCommentId) so both top-level
 * posting and replying share the same composer/submit logic.
 */
export default function CommentSection({ getComments, createComment, deleteOwnComment, currentUserId }) {
  const [comments, setComments] = useState(null);
  const [draft, setDraft] = useState('');
  const [draftImageUrl, setDraftImageUrl] = useState(null);
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null); // comment id, or null
  const [replyDraft, setReplyDraft] = useState('');
  const [replyImageUrl, setReplyImageUrl] = useState(null);
  const [replying, setReplying] = useState(false);

  const load = useCallback(async () => {
    try { setComments(await getComments()); } catch (err) { setError(err.response?.data?.error || 'Could not load comments.'); }
  }, [getComments]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!draft.trim()) return;
    setPosting(true);
    setError(null);
    try {
      await createComment(draft.trim(), undefined, draftImageUrl);
      setDraft('');
      setDraftImageUrl(null);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not add comment.');
    } finally {
      setPosting(false);
    }
  };

  const submitReply = async (parentId) => {
    if (!replyDraft.trim()) return;
    setReplying(true);
    setError(null);
    try {
      await createComment(replyDraft.trim(), parentId, replyImageUrl);
      setReplyDraft('');
      setReplyImageUrl(null);
      setReplyingTo(null);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not add your reply.');
    } finally {
      setReplying(false);
    }
  };

  const remove = async (commentId) => {
    if (!window.confirm('Delete this comment?')) return;
    try {
      await deleteOwnComment(commentId);
      setComments(prev => prev.filter(c => c.id !== commentId && c.parent_comment_id !== commentId));
    } catch (err) {
      setError(err.response?.data?.error || 'Could not delete comment.');
    }
  };

  const topLevel = comments ? comments.filter(c => !c.parent_comment_id) : [];
  const repliesByParent = {};
  if (comments) {
    comments.filter(c => c.parent_comment_id).forEach(c => {
      (repliesByParent[c.parent_comment_id] ||= []).push(c);
    });
  }
  const totalCount = comments ? comments.length : 0;

  return (
    <div className="mt-6">
      <h3 className="text-sm font-bold text-reset-brown mb-3">
        {comments ? `${totalCount} Comment${totalCount === 1 ? '' : 's'}` : 'Comments'}
      </h3>

      {error && <div className="mb-3 bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 text-xs">{error}</div>}

      <form onSubmit={submit} className="bg-white rounded-2xl shadow-sm p-4 mb-4">
        <MentionInput
          value={draft}
          onChange={setDraft}
          placeholder="Add a comment… (type @ to tag someone, or paste a link)"
          rows={2}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
        />
        <div className="mt-2 max-w-xs">
          <ImageDropZone currentUrl={draftImageUrl} onUploaded={setDraftImageUrl} onClear={() => setDraftImageUrl(null)} />
        </div>
        <div className="flex justify-end mt-2">
          <button type="submit" disabled={posting || !draft.trim()} className="px-4 py-2 bg-reset-terracotta text-white text-sm font-medium rounded-xl hover:bg-reset-brown transition-colors disabled:opacity-50">
            {posting ? 'Posting…' : 'Post comment'}
          </button>
        </div>
      </form>

      {!comments ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : topLevel.length === 0 ? (
        <p className="text-sm text-gray-400">No comments yet — be the first.</p>
      ) : (
        <div className="space-y-3">
          {topLevel.map(c => (
            <div key={c.id}>
              <CommentRow
                comment={c}
                isOwn={c.member_id === currentUserId}
                onDelete={() => remove(c.id)}
                onReply={() => { setReplyingTo(c.id); setReplyDraft(''); setReplyImageUrl(null); setError(null); }}
              />

              {(repliesByParent[c.id] || []).map(r => (
                <div key={r.id} className="ml-8 mt-2">
                  <CommentRow
                    comment={r}
                    isOwn={r.member_id === currentUserId}
                    onDelete={() => remove(r.id)}
                    onReply={() => { setReplyingTo(c.id); setReplyDraft(''); setReplyImageUrl(null); setError(null); }}
                  />
                </div>
              ))}

              {replyingTo === c.id && (
                <div className="ml-8 mt-2 bg-white rounded-2xl shadow-sm p-3">
                  <MentionInput
                    value={replyDraft}
                    onChange={setReplyDraft}
                    placeholder="Write a reply… (type @ to tag someone, or paste a link)"
                    rows={2}
                    autoFocus
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
                  />
                  <div className="mt-2 max-w-xs">
                    <ImageDropZone currentUrl={replyImageUrl} onUploaded={setReplyImageUrl} onClear={() => setReplyImageUrl(null)} />
                  </div>
                  <div className="flex justify-end gap-2 mt-2">
                    <button onClick={() => setReplyingTo(null)} className="text-xs text-gray-400 hover:text-reset-brown">Cancel</button>
                    <button
                      onClick={() => submitReply(c.id)}
                      disabled={replying || !replyDraft.trim()}
                      className="px-3 py-1.5 bg-reset-terracotta text-white text-xs font-medium rounded-xl hover:bg-reset-brown transition-colors disabled:opacity-50"
                    >
                      {replying ? 'Replying…' : 'Reply'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CommentRow({ comment: c, isOwn, onDelete, onReply }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm p-4 flex items-start gap-3">
      {c.author?.avatar_url ? (
        <img src={c.author.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover shrink-0" />
      ) : (
        <div className="w-8 h-8 rounded-full bg-reset-sage text-white flex items-center justify-center text-xs font-bold shrink-0">
          {(c.author?.name || '?').trim().charAt(0).toUpperCase()}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-reset-brown">{c.author?.name || '(deleted member)'}</span>
          <span className="text-[11px] text-gray-400">{new Date(c.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>
        </div>
        <div className="text-sm text-gray-700 mt-0.5 [&_p]:mb-0">{renderRichText(c.body)}</div>
        {c.image_url && (
          <img src={c.image_url} alt="" className="max-h-56 rounded-lg object-cover mt-2" />
        )}
        <button onClick={onReply} className="text-[11px] font-medium text-gray-400 hover:text-reset-terracotta mt-1">
          Reply
        </button>
      </div>
      {isOwn && (
        <button onClick={onDelete} className="text-[11px] text-gray-300 hover:text-red-500 shrink-0">
          Delete
        </button>
      )}
    </div>
  );
}
